import { Component, Injector, Input, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Card, CardPriorityEnum, CardService, CardTemperatureEnum, Funnel, Pager, PRIORITY_LABEL, Product, QUALIFICATION_LABEL, Stage, TEMPERATURE_LABEL, URGENCY_CONTEXT, URGENCY_ICON, URGENCY_LABEL, stagnantTooltipKey, taskTypeLabelKey } from 'lib-trend-core';
import { combineLatest, filter, finalize, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { AppCRMState, ParamsState } from '../state/app.state';
import { Store } from '@ngrx/store';
import { funnelSelector, queryParamsSelector } from '../state/selectors';
import { CardFormComponent, CardFormComponentData } from '../card-form/card-form.component';
import { MatDialog } from '@angular/material/dialog';
import { TransferCardComponent } from '../transfer-card/transfer-card.component';
import { CardDetailsComponent } from '../card-details/card-details.component';
import { DeleteDealDialogComponent, DeleteDealDialogData } from '../delete-deal-dialog/delete-deal-dialog.component';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../../task/activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';

@Component({
    selector: 'app-card-list',
    templateUrl: './card-list.component.html',
    styleUrl: './card-list.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardListComponent extends AbstractComponent {
  @Input() stage: Stage;
  public pager: Pager<Card> = new Pager<Card>({ perPage: 10 });
  public selectedFunnel!: Funnel;

  private readonly reload$ = new Subject<void>();

  readonly funnel$: Observable<Funnel> = this.store.select(funnelSelector);
  readonly queryParams$: Observable<ParamsState> = this.store.select(queryParamsSelector);

  temperatures = Object.values(CardTemperatureEnum).map(v => ({ value: v, label: TEMPERATURE_LABEL[v] }));
  readonly CardTemperatureEnum = CardTemperatureEnum;

  constructor(
    injector: Injector,
    private cardService: CardService,
    private store: Store<AppCRMState>,
    public dialog: MatDialog,
  ) {
    super(injector)
  }

  ngOnInit(): void {
    combineLatest([this.funnel$, this.queryParams$]).pipe(
      takeUntil(this.destroy$),
      filter(([funnel]) => !!funnel?._id),
    ).subscribe(([funnel, params]) => {
      this.selectedFunnel = funnel;
      this.searchString = params?.search;
      this.searchParams = params?.params;
      this.pager.page = 1;
      this.getCards();
    });
  }

  getCards() {
    this.loadingContent = true;

    const params = {
      ...this.searchParams,
      funnel: this.selectedFunnel._id,
    }

    this.cardService.getListCardsPaginated(this.pager.page, this.pager.perPage, this.searchString, params)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: (cards: Pager<Card>) => {
          this.pager = cards;
        }
      });
  }

  translateEnums(value: string): string {
    return PRIORITY_LABEL[value as CardPriorityEnum] ?? 'kanban.cardList.priority';
  }


  traslateTemperature(value: string): string {
    return TEMPERATURE_LABEL[value as CardTemperatureEnum];
  }

  getCardStage(stage: Stage) { }

  loadPage(page: number) {
    this.pager.page = page;
    this.getCards();
  }

  getProdutctsNames(products: Array<Product>) {
    return products.map(p => p.name).join(', ') || 'Sem Produtos'
  }

  openModalCard(stage: Stage, card?: Card) {
    if (!this.selectedFunnel?._id) return;

    const data: CardFormComponentData = { idStage: stage._id, idFunnel: this.selectedFunnel._id, card };

    this.dialog.open(CardFormComponent, { width: '1000px', data: data }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: Card) => {
      if (!result) return;

      if (card) {
        const index = this.pager.list?.findIndex(c => c._id === result._id) ?? -1;
        if (index !== -1 && this.pager.list) {
          this.pager.list[index] = result;
        }
      } else {
        this.pager.list = [result, ...(this.pager.list || [])];
        this.pager.total = (this.pager.total || 0) + 1;
      }
    });
  }

  openTransferCardModal(event: MouseEvent, card: Card, action: 'move' | 'clone') {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(TransferCardComponent, {
      data: { idStage: card.stage, card, action },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }

  openDetailCard(card: Card) {
    return this.dialog.open(CardDetailsComponent, { width: '1000px', data: card });
  }

  /**
   * The dialog deletes the deal itself, after asking what becomes of its open activities
   * (E4 RF-51); it closes with a result only once the deal is gone.
   */
  deleteCard(card: Card): void {
    const data: DeleteDealDialogData = { card };
    this.dialog.open(DeleteDealDialogComponent, { width: '560px', data })
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter(result => !!result)
      )
      .subscribe(() => {
        this.alertService?.success(this.t('kanban.cardList.deleteSuccess'));
        this.pager.list = this.pager.list?.filter(c => c._id !== card._id) || [];
        this.getCards();
      });
  }

  /**
   * A won or lost deal has no pending commitment, so urgency and stagnation are
   * meaningless on it — the same rule the board card applies (RF-13).
   */
  isClosed(card: Card): boolean {
    return card?.won !== null && card?.won !== undefined;
  }

  urgencyLabel(card: Card): string {
    return URGENCY_LABEL[card?.urgency] ?? 'kanban.urgency.scheduled';
  }

  urgencyIcon(card: Card): string {
    return URGENCY_ICON[card?.urgency] ?? 'ri-calendar-check-line';
  }

  urgencyContext(card: Card): string {
    return URGENCY_CONTEXT[card?.urgency] ?? 'secondary';
  }

  qualificationLabel(card: Card): string {
    return QUALIFICATION_LABEL[card?.leadQuality?.qualification] ?? 'kanban.qualification.unqualified';
  }

  lastInteractionLabel(card: Card): string {
    const type = card?.lastInteraction?.type;
    // A message carries no task type: the source itself is the label (RF-10).
    if (type === 'MESSAGE') return 'kanban.radar.interactionMessage';
    return taskTypeLabelKey(type);
  }

  /** Same tooltip the board card shows, from the same shared key resolver (RF-15). */
  stagnantTooltip(card: Card): string {
    return stagnantTooltipKey(card?.lastInteraction?.direction);
  }

  /** `d MMM` for a commitment with no set time (E4 RF-05); the time otherwise. */
  nextActionFormat(card: Card): string {
    return card?.nextAction?.untimed === true ? 'd MMM' : 'd MMM, HH:mm';
  }

  /** E4 RF-17: the list's next-action line opens the conclusion of that activity, like the board card. */
  openConcludeDialog(card: Card, event: Event): void {
    event.stopPropagation();
    const idTask = card?.nextAction?.task;
    if (!idTask) return;

    this.dialog.open(ConcludeActivityDialogComponent, { data: { idTask, card }, width: '720px', disableClose: true })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ConcludeActivityDialogResult | undefined) => {
        if (result) this.getCards();
      });
  }
}
